// Mock notifications dataset for the v1 NotificationsDropdown.
//
// Replaced by the real notifications backend per cutover-checklist
// §1. Until then, this module exports a small fixed list that
// represents the eight notification types from shared-chrome-v2 §4
// so design + UX review can see realistic content.
//
// PURE module — no PII (the names are clearly synthetic), no real
// IDs. Safe to commit to git.

export type NotificationKind =
  | "mention"
  | "task_assigned"
  | "task_due"
  | "data_needed"
  | "stale_record"
  | "opportunity_stale"
  | "rhythm_touchpoint"
  | "automation_event"
  // Form / intake submission landed (persona_key='system' bell events,
  // P0-8). Kept in sync with shared/db/notifications.ts's NotificationKind.
  | "form_submission"
  // Microsoft Bookings appointment landed (new contact created, or the
  // booking's contact details differed from ours and we updated them).
  | "booking"
  // A signature request ended (declined, or fully signed).
  | "esign"
  // A teammate asked you for documents, or dropped one you asked for
  // (Documents tab PR 6a).
  | "document_request"
  // A Company Coverage policy was shared with you.
  | "shared_with_you"
  // An OS health alert for system admins (Email Health).
  | "system_health"
  | "ai_suggestion";

export interface MockNotification {
  id: string;
  kind: NotificationKind;
  title: string;
  context: string;
  timeLabel: string;
  // Where clicking the notification routes. The real backend will carry
  // a per-notification target (the ask_stew_nudges table already models
  // this with an `href` column); until then the mock rows point at the
  // most relevant surface so the click isn't a dead end (QA NEW-08b).
  href: string;
  // Whether the signed-in person has opened or cleared it, as the server
  // remembers it (notification_reads). Absent on the mock rows below.
  read?: boolean;
}

export const MOCK_NOTIFICATIONS: readonly MockNotification[] = [
  {
    id: "n-mention-1",
    kind: "mention",
    title: "Jake mentioned you in a note",
    context:
      "On The Smith Family — \"@Grant — can you approve the umbrella rewrite?\"",
    timeLabel: "12m ago",
    href: "/watchlist?focus=at-risk",
  },
  {
    id: "n-task-assigned-1",
    kind: "task_assigned",
    title: "Task assigned · due Monday",
    context: "Review Q2 data hygiene — 12 records flagged by Brian Baker.",
    timeLabel: "3h ago",
    href: "/tasks",
  },
  {
    id: "n-ai-1",
    kind: "ai_suggestion",
    title: "AI suggestion · 4 items for Today",
    context:
      "The weekly watchlist found 4 households drifting out of cadence.",
    timeLabel: "this morning",
    href: "/watchlist?focus=at-risk",
  },
  {
    id: "n-data-1",
    kind: "data_needed",
    title: "Data needed · 3 households",
    context:
      "Three households missing fit assessment for Insurance — Joy assigned.",
    timeLabel: "Yesterday",
    href: "/watchlist?focus=at-risk",
  },
  {
    id: "n-rhythm-1",
    kind: "rhythm_touchpoint",
    title: "Care plan step due tomorrow",
    context: "Smith Family Renewal Plan — carrier shop call.",
    timeLabel: "Yesterday",
    href: "/ongoing-care",
  },
];

export type NotificationTab =
  | "all"
  | "mentions"
  | "tasks"
  | "archive";

export const NOTIFICATION_TABS: readonly {
  value: NotificationTab;
  label: string;
}[] = [
  { value: "all", label: "All" },
  { value: "mentions", label: "Mentions" },
  { value: "tasks", label: "Tasks" },
  { value: "archive", label: "Archive" },
];

// Filter the supplied list to the current tab. Pure function so the
// node-pool tests can verify the tab → kind mapping.
export function filterNotificationsByTab(
  notifications: readonly MockNotification[],
  tab: NotificationTab,
): MockNotification[] {
  switch (tab) {
    case "all":
      return [...notifications];
    case "mentions":
      return notifications.filter((n) => n.kind === "mention");
    case "tasks":
      return notifications.filter(
        (n) =>
          n.kind === "task_assigned" || n.kind === "task_due" || n.kind === "document_request",
      );
    // No "AI" tab: proactive AI suggestions live in the Ask Stew nudge
    // banner now, not the bell (P0-8 ownership split, 2026-07-24).
    case "archive":
      // No archived items in the mock dataset; the empty-state
      // copy fires.
      return [];
  }
}
